import { BaseFilters } from '@/models/Service';
import { isArray } from 'lodash';

type ConfigField = {
  isArray?: boolean;
  type: 'string' | 'number';
};

export type Fields<T extends object> = Partial<{
  [key in keyof T]: ConfigField;
}>;

const defaultNumberParams: Fields<BaseFilters> = {
  pageNumber: { type: 'number' },
  pageSize: { type: 'number' },
};

export function searchParamsToObject<T extends BaseFilters>(
  input: URLSearchParams | Partial<T>,
  configFields: Fields<T>,
  initialValues: Partial<T> = {},
): T {
  configFields = {
    ...defaultNumberParams,
    ...configFields,
  };
  const filter: T = { ...initialValues } as T;

  if (input instanceof URLSearchParams) {
    input.forEach((value, key) => {
      processKey(filter, key as keyof T, value, configFields);
    });
  } else {
    Object.entries(input).forEach(([key, value]) => {
      processKey(filter, key as keyof T, value, configFields);
    });
  }

  return filter;
}

function processKey<T extends BaseFilters>(
  filter: T,
  key: keyof T,
  value: any,
  configFields: Fields<T>,
) {
  if (!(key in configFields)) {
    filter[key] = value as any;
    return;
  }

  const fieldConfig = configFields[key];

  if (value === undefined || value === null) {
    filter[key] = value as any;
    return;
  }

  const isNumber = fieldConfig?.type === 'number';
  const convertedValue = isNumber
    ? isNaN(Number(value))
      ? null
      : Number(value)
    : value;

  if (fieldConfig?.isArray) {
    filter[key] = [
      ...(isArray(filter[key]) ? (filter[key] as any) : []),
      convertedValue,
    ] as any;
  } else {
    filter[key] = convertedValue as any;
  }
}
